<script lang="ts" setup>
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Section } from '~/types'

interface Props extends Pick<Section, 'title' | 'subtitle' | 'description'> {}
const props = defineProps<Props & { inline?: boolean, title?: string, subtitle?: string, description?: string }>()

const emits = defineEmits(['loggedInUser', 'loggedInAdmin'])

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
})

type LoginSchema = z.output<typeof loginSchema>
const toast = useToast()
const loginDetails = reactive({
  email: '',
  password: '',
})
const router = useRouter()
const { userId } = useUser()
const toGoTo = ref('/dashboard')
const loading = ref(false)
async function login(event: FormSubmitEvent<LoginSchema>) {
  loading.value = true
  const pb = useNuxtApp().$pb
  let userLoginSuccess = false
  let adminLoginSuccess = false
  let lastError: any = null

  // Try user login
  try {
    await pb.collection('users').authWithPassword(
      event.data.email,
      event.data.password,
    )
    userLoginSuccess = true

    if (!props.inline) {
      const prev = router.options.history.state?.back as string
      if (prev && prev.includes('/dashboard/')) {
        toGoTo.value = prev
      }
      if (userId.value) {
        navigateTo(toGoTo.value)
      }
    }
    emits('loggedInUser')
  }
  catch (e: any) {
    lastError = e
  }

  // Try admin login
  try {
    await pb.collection('admins').authWithPassword(
      event.data.email,
      event.data.password,
    )
    adminLoginSuccess = true

    if (!props.inline) {
      navigateTo('/admin')
    }
    emits('loggedInAdmin')
  }
  catch (e: any) {
    lastError = e
  }
  loading.value = false
  // If both failed, show error
  if (!userLoginSuccess && !adminLoginSuccess) {
    toast.add({
      id: 'login-error',
      title: 'Login Error',
      description: lastError?.data?.message ?? 'Unknown error',
    })
  }
}

const passwordVisible = ref(false)

if (!props.inline) {
  useSeoMeta({
    title: props.title,
  })
}
</script>

<template>
  <div :class="[inline ? '' : 'pt-32 w-full min-h-[80vh] flex flex-col items-center justify-center ']">
    <template v-if="!inline">
      <HeadingText
        tag="h1"
        class="pb-6 text-center"
      >
        {{ title }}
      </HeadingText>
      <div
        class="htmldescription pb-12  max-w-4xl"
        data-allow-mismatch
        v-html="props.description"
      />
    </template>
    <UForm
      :schema="loginSchema"
      :state="loginDetails"
      class="mx-auto"
      :class="[inline ? 'w-full max-w-sm ' : 'w-full max-w-sm']"
      @submit="login"
    >
      <UFormGroup
        label="Email"
        class="mb-3"
        name="email"
      >
        <UInput
          v-model="loginDetails.email"
          :loading="loading"
          type="text"
          placeholder="Email"
          size="lg"
          color="white"
          :ui="{ form: '!bg-white' }"
        />
      </UFormGroup>
      <UFormGroup
        label="Password"
        class="mb-3"
        name="password"
      >
        <UButtonGroup
          class="w-full"
          size="lg"
          :ui="{ rounded: 'rounded-full' }"
        >
          <UInput
            v-model="loginDetails.password"
            :loading="loading"
            :type="passwordVisible ? 'string' : 'password'"
            size="lg"
            placeholder="Password"
            class="flex-1"
            color="white"
            :ui="{ form: '!bg-white' }"
          />
          <UButton
            v-if="passwordVisible"
            tabindex="-1"
            size="lg"
            type="button"
            icon="heroicons:eye-slash-16-solid"
            color="gray"
            aria-label="hide password"
            @click="passwordVisible = !passwordVisible"
          />
          <UButton
            v-else
            tabindex="-1"
            type="button"
            size="lg"
            icon="heroicons:eye-16-solid"
            color="primary"
            aria-label="show password"
            @click="passwordVisible = !passwordVisible"
          />
        </UButtonGroup>
      </UFormGroup>
      <div class="flex justify-between items-center">
        <UButton
          type="submit"
          size="xl"
          icon="gbf:arrow-right"
          trailing
          :loading="loading"
        >
          Login
        </UButton>
        <UButton
          type="button"
          variant="link"
          :to="`/forgotten-password?email=${loginDetails.email}`"
        >
          Forgotten Password?
        </UButton>
      </div>
      <UButton
        type="button"
        variant="outline"
        class="mt-6"
        to="/register"
        block
        size="lg"
      >
        Not a member? Register now.
      </UButton>
    </UForm>
  </div>
</template>
